// ===== Packages =====

import ShortUniqueId                        from 'short-unique-id';

// ===== Firebase =====

import fire                                 from '../fire';

// ===== Services =====

import {
    createAnonymousUser,
    getUser,
    inferNameFromEmail,
    migrateOldUserDataToNewItem,
    sendNewAccountOnboardingEmail,
    updateUser,
    updateUserAuthenticationType,
}                                           from '.';
import {
    logAuthDebug,
}                                           from './authDebug';
import resolvePreferredUserName             from './resolvePreferredUserName';

// ===== Interfaces =====

import {
    IAnnotationCommentSubscriptionValue,
    IAuthenticatedUserSetupOldUserData,
    IMailingListSubscriptionValue,
}                                           from '../interfaces';

// ===== Enums =====

import {
    AUTHENTICATION_TYPE,
    FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE,
}                                           from '../enums';

// ===== Constants =====

import {
    DEFAULT_USER_FIRST_NAME,
}                                           from '../constants/generalConstants';

const { auth } = fire();
const ONBOARDING_EMAIL_RETRY_DELAY_MS = 1000;
const USER_DEFAULTS_WRITE_RETRY_DELAY_MS = 300;

const sendOnboardingEmailBestEffort = ({
    test,
    temporaryPassword,
}: {
    test: boolean,
    temporaryPassword?: string,
}): void => {
    sendNewAccountOnboardingEmail({
        test,
        temporaryPassword,
    }).catch(() => {
        setTimeout(() => {
            sendNewAccountOnboardingEmail({
                test,
                temporaryPassword,
            }).catch(() => null);
        }, ONBOARDING_EMAIL_RETRY_DELAY_MS);
    });
};

/**
 * Forces Firebase Auth to emit a fresh token event after the user document has
 * been updated. Anonymous-to-email linking can otherwise notify App before the
 * Firestore user item has stopped looking anonymous, leaving the header and
 * reader stuck until a reload or later token refresh.
 */
const refreshAuthTokenAfterUserWrites = ({
    userId,
    logSetupEvent,
}: {
    userId: string,
    logSetupEvent: (event: string, data?: Record<string, unknown>) => void,
}): void => {
    const latestAuthUser = auth.currentUser;
    if (!latestAuthUser || latestAuthUser.uid !== userId) return;

    latestAuthUser.getIdToken(true)
        .then(() => {
            logSetupEvent('auth-setup-post-write-token-refreshed');
        })
        .catch((error) => {
            logSetupEvent('auth-setup-post-write-token-refresh-failed', {
                message: (error as Error)?.message || String(error),
            });
        });
};

const completeAuthenticatedUserSetup = async ({
    test,
    oldUserData,
    onboardingTemporaryPassword,
}: {
    test: boolean,
    oldUserData?: IAuthenticatedUserSetupOldUserData,
    onboardingTemporaryPassword?: string,
}): Promise<void> => {
    const { currentUser } = auth;
    if (!currentUser) {
        throw Error('Unable to complete sign-in because the authenticated user could not be resolved.');
    }
    const startedAt = Date.now();
    const logSetupEvent = (
        event: string,
        data?: Record<string, unknown>,
    ): void => {
        logAuthDebug(event, {
            userId: currentUser.uid,
            elapsedMs: Date.now() - startedAt,
            ...data,
        });
    };
    logSetupEvent('auth-setup-started', {
        hasOldUser: !!oldUserData?.user,
        oldUserId: oldUserData?.user?.id || null,
    });

    const shouldForceTokenRefresh = !!(
        oldUserData?.user
        && oldUserData.user.id === currentUser.uid
        && oldUserData.user.authenticationType === AUTHENTICATION_TYPE.anonymous
    );
    // Anonymous -> linked account can keep the same UID; force refresh ensures
    // callable auth context gets the new sign-in provider instead of stale "anonymous".
    await currentUser.getIdToken(shouldForceTokenRefresh).catch(() => null);
    logSetupEvent('auth-setup-token-ready', {
        shouldForceTokenRefresh,
    });

    const existingUser = await getUser({
        test,
        userId: currentUser.uid,
    });
    logSetupEvent('auth-setup-existing-user-fetched', {
        hasExistingUser: !!existingUser,
        authenticationType: existingUser?.authenticationType || null,
    });

    const [providerUser] = currentUser.providerData;
    const providerId = providerUser?.providerId;
    const isSocialProvider = !!(
        providerId
        && providerId !== FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.email
        && providerId !== FIREBASE_AUTH_SIGN_IN_PROVIDER_ID_TYPE.anonymous
    );
    const email = currentUser.email || providerUser?.email || oldUserData?.email || undefined;
    const inferredEmailName = email
        ? inferNameFromEmail(email)
        : {};
    const isAnonymousUpgradeSameUid = !!(
        oldUserData?.user
        && oldUserData.user.id === currentUser.uid
        && oldUserData.user.authenticationType === AUTHENTICATION_TYPE.anonymous
    );
    let firstName = DEFAULT_USER_FIRST_NAME;
    let lastName = new ShortUniqueId({ length: 6 })(); // avoid anonymous users with same names
    if (providerUser?.displayName) {
        const displayNameParts = providerUser.displayName
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        if (displayNameParts.length > 0) {
            [firstName] = displayNameParts;
        }
        if (displayNameParts.length > 1) {
            lastName = displayNameParts.slice(1).join(' ');
        }
    } else if (oldUserData?.user) {
        // Preserve explicit checkout/profile names, but let inferred email names
        // replace obvious anonymous placeholder names when that is all we have.
        if (isAnonymousUpgradeSameUid && !isSocialProvider) {
            const resolvedName = resolvePreferredUserName({
                preferredFirstName: oldUserData.user.firstName,
                preferredLastName: oldUserData.user.lastName,
                fallbackFirstName: inferredEmailName.firstName,
                fallbackLastName: inferredEmailName.lastName,
            });
            firstName = resolvedName.firstName || firstName;
            lastName = resolvedName.lastName || lastName;
        } else {
            firstName = oldUserData.user.firstName || firstName;
            lastName = oldUserData.user.lastName || lastName;
        }
    }

    if (!existingUser) {
        logSetupEvent('auth-setup-create-user-started');
        await createAnonymousUser({
            test,
            firstName,
            lastName,
            phoneNumber: providerUser?.phoneNumber || undefined,
            photoURL: providerUser?.photoURL || undefined,
        });
        logSetupEvent('auth-setup-create-user-succeeded');
    }

    const shouldMigrateOldUser = (
        !!oldUserData?.user
        && oldUserData.user.id !== currentUser.uid
    );
    if (shouldMigrateOldUser && oldUserData?.user) {
        const migrationPromise = migrateOldUserDataToNewItem({
            test,
            oldUser: oldUserData.user,
            oldUserEmail: oldUserData.email,
            oldUserCart: oldUserData.oldUserCart,
            oldUserSessions: oldUserData.userSessions,
            oldUserUpdateTimestamps: oldUserData.userUpdateTimestamps,
        });
        logSetupEvent('auth-setup-migration-started', {
            oldUserId: oldUserData.user.id,
        });
        // Do not block auth setup on migration callable latency.
        // Migration remains server-side and eventual; listeners reconcile the local user when complete.
        migrationPromise.then(() => {
            logSetupEvent('auth-setup-migration-completed');
        }).catch((error) => {
            logSetupEvent('auth-setup-migration-background-failed', {
                message: (error as Error)?.message || String(error),
            });
        });
        logSetupEvent('auth-setup-migration-backgrounded');
    }
    const criticalWriteTasks: Promise<unknown>[] = [
        updateUserAuthenticationType({
            test,
        }),
    ];
    const backgroundWriteTasks: Promise<unknown>[] = [];
    const updateUserWithRetry = async (
        payload: Omit<Parameters<typeof updateUser>[0], 'test'>,
    ): Promise<void> => {
        try {
            await updateUser({
                test,
                ...payload,
            });
        } catch (error) {
            const errorMessage = (error as Error)?.message || String(error);
            if (!errorMessage.includes('User doesn\'t exist')) {
                throw error;
            }
            await new Promise((resolve) => {
                setTimeout(resolve, USER_DEFAULTS_WRITE_RETRY_DELAY_MS);
            });
            await updateUser({
                test,
                ...payload,
            });
        }
    };

    const shouldSyncProviderProfile = !!(
        isSocialProvider
        && (
            !existingUser
            || existingUser.authenticationType === AUTHENTICATION_TYPE.anonymous
        )
    );
    const shouldProvisionAuthenticatedDefaults = !!(
        !existingUser
        || existingUser.authenticationType === AUTHENTICATION_TYPE.anonymous
    );
    const shouldSyncEmailConversionProfile = !!(
        isAnonymousUpgradeSameUid
        && !isSocialProvider
        && !!oldUserData?.user
    );
    // Shared safety net for anon -> social upgrades (including link-with-popup/redirect):
    // even when UID is reused, provider profile fields should replace anon placeholder names.
    const userMetadataUpdates: {
        email?: string,
        firstName?: string,
        lastName?: string,
        phoneNumber?: string,
        photoURL?: string,
    } = {};
    if (email) {
        userMetadataUpdates.email = email;
    }
    if (shouldSyncProviderProfile) {
        if (firstName) {
            userMetadataUpdates.firstName = firstName;
        }
        if (lastName) {
            userMetadataUpdates.lastName = lastName;
        }
        if (providerUser?.phoneNumber) {
            userMetadataUpdates.phoneNumber = providerUser.phoneNumber;
        }
        if (providerUser?.photoURL) {
            userMetadataUpdates.photoURL = providerUser.photoURL;
        }
    }
    if (shouldSyncEmailConversionProfile) {
        // For anonymous -> email conversion (same UID), apply inferred/profile names.
        // This keeps Auth Lock conversion aligned with expected profile personalization.
        if (firstName) {
            userMetadataUpdates.firstName = firstName;
        }
        if (lastName) {
            userMetadataUpdates.lastName = lastName;
        }
    }
    if (Object.keys(userMetadataUpdates).length > 0) {
        const shouldAwaitMetadataSync = !!(
            !existingUser
            || shouldSyncProviderProfile
            || shouldSyncEmailConversionProfile
        );
        if (shouldAwaitMetadataSync) {
            // Auth-lock/provider setup should surface provider name/avatar immediately
            // after sign-in without requiring a manual reload.
            criticalWriteTasks.push(updateUserWithRetry({
                ...userMetadataUpdates,
            }));
        } else {
            backgroundWriteTasks.push(updateUserWithRetry({
                ...userMetadataUpdates,
            }));
        }
    }

    if (shouldProvisionAuthenticatedDefaults) {
        const subscriptionTimestamp = Date.now();
        const existingMailingListHistory = (
            existingUser?.mailingListSubscription?.history
            || oldUserData?.user?.mailingListSubscription?.history
            || []
        );
        const existingCommentHistory = (
            existingUser?.commentSubscription?.history
            || oldUserData?.user?.commentSubscription?.history
            || []
        );

        const mailingListSubscriptionToAdd: IMailingListSubscriptionValue[] = [];
        if (existingMailingListHistory.length === 0) {
            if (!existingUser) {
                // New users are provisioned via createAnonymousUser with [false] already present.
                // Add only the authenticated opt-in to avoid duplicate false entries.
                mailingListSubscriptionToAdd.push({
                    subscribed: true,
                    timestamp: subscriptionTimestamp,
                });
            } else {
                mailingListSubscriptionToAdd.push(
                    {
                        subscribed: false,
                        timestamp: subscriptionTimestamp,
                    },
                    {
                        subscribed: true,
                        timestamp: subscriptionTimestamp,
                    },
                );
            }
        } else if (!existingMailingListHistory[existingMailingListHistory.length - 1].subscribed) {
            mailingListSubscriptionToAdd.push({
                subscribed: true,
                timestamp: subscriptionTimestamp,
            });
        }

        const commentSubscriptionToAdd: IAnnotationCommentSubscriptionValue[] = [];
        if (existingCommentHistory.length === 0) {
            if (!existingUser) {
                // New users are provisioned via createAnonymousUser with [false] already present.
                // Add only the authenticated opt-in to avoid duplicate false entries.
                commentSubscriptionToAdd.push({
                    subscribed: true,
                    timestamp: subscriptionTimestamp,
                });
            } else {
                commentSubscriptionToAdd.push(
                    {
                        subscribed: false,
                        timestamp: subscriptionTimestamp,
                    },
                    {
                        subscribed: true,
                        timestamp: subscriptionTimestamp,
                    },
                );
            }
        } else if (!existingCommentHistory[existingCommentHistory.length - 1].subscribed) {
            commentSubscriptionToAdd.push({
                subscribed: true,
                timestamp: subscriptionTimestamp,
            });
        }

        if (
            mailingListSubscriptionToAdd.length > 0
            || commentSubscriptionToAdd.length > 0
        ) {
            // These defaults are expected to be present right after sign-up (including redirect flows).
            // Keep this on the critical path so UI hydration doesn't race a background write.
            criticalWriteTasks.push(updateUserWithRetry({
                mailingListSubscriptionToAdd,
                commentSubscriptionToAdd,
            }));
        }
    }

    logSetupEvent('auth-setup-post-auth-writes-started', {
        criticalTaskCount: criticalWriteTasks.length,
        backgroundTaskCount: backgroundWriteTasks.length,
    });
    await Promise.all(criticalWriteTasks);
    logSetupEvent('auth-setup-post-auth-writes-succeeded', {
        completedTaskCount: criticalWriteTasks.length,
    });
    refreshAuthTokenAfterUserWrites({
        userId: currentUser.uid,
        logSetupEvent,
    });
    if (backgroundWriteTasks.length > 0) {
        // Non-critical profile/default-sync writes should not block auth completion.
        Promise.all(backgroundWriteTasks).then(() => {
            logSetupEvent('auth-setup-post-auth-writes-backgrounded', {
                completedTaskCount: backgroundWriteTasks.length,
            });
        }).catch((error) => {
            logSetupEvent('auth-setup-post-auth-writes-background-failed', {
                message: (error as Error)?.message || String(error),
            });
        });
    }

    const shouldSendOnboardingEmail = !!(
        shouldProvisionAuthenticatedDefaults
        && email
    );
    if (shouldSendOnboardingEmail) {
        // Best effort only. Do not block auth completion on email queueing/retry latency.
        sendOnboardingEmailBestEffort({
            test,
            temporaryPassword: onboardingTemporaryPassword,
        });
        logSetupEvent('auth-setup-onboarding-email-enqueued');
    }
    logSetupEvent('auth-setup-completed');
};

export default completeAuthenticatedUserSetup;
